import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Col, Empty, Modal, Row, Spin } from 'antd';
import cn from 'classnames';

import { getWarehouse } from '@/api/warehouse';
import { NearbyWarehouseCard } from '@/components/PersonalAccountWarehouses/NearbyWarehouseCard';
import FishToBuyResultHeader from '@/components/SearchFishToBuy/FishToBuyResultHeader';
import { WarehousesMap } from '@/components/WarehousesMap';
import { useUserState } from '@/context/user.context';
import useAsyncEffect from '@/hooks/use-async-effect';
import { TranslationNamespace } from '@/i18n';
import { SnowflakeIcon } from '@/icons';
import { SearchedWarehouse } from '@/types/dadata';
import { Storage } from '@/types/storage';
import { WarehouseSearchResult } from '@/types/warehouses';

import { SearchWarehousesFilters } from '../../SearchWarehouses';
import { BannerForRegistration } from '../BannerForRegistration/BannerForRegistration';
import { WarehouseModalContent } from '../WarehouseModalContent/WarehouseModalContent';

import styles from './WarehousesList.module.scss';

type Props = {
	isLoading: boolean;
	isLoadingMore: boolean;
	count?: number;
	warehouses: WarehouseSearchResult['results'];
	hasMore: boolean;
	loadMoreDisabled: boolean;
	isShownNearWarehouses?: boolean;
	filters?: SearchWarehousesFilters;
	onLoadMore: (page: number) => void;
	getFilters: () => SearchWarehousesFilters;
};

export const WarehousesList: FC<Props> = ({
	isLoading,
	isLoadingMore,
	hasMore,
	loadMoreDisabled,
	warehouses,
	count,
	isShownNearWarehouses,
	onLoadMore,
}) => {
	const { t } = useTranslation(TranslationNamespace.WarehouseSearch);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedWarehouse, setSelectedWarehouse] = useState<SearchedWarehouse>(null);
	const [warehouseDetails, setWarehouseDetails] = useState<Storage>(null);
	const [isLoadingWarehouseDetails, setIsLoadingWarehouseDetails] = useState(false);
	const [highlightedWarehouseId, setHighlightedWarehouseId] = useState<string>(null);
	const highlightTimerRef = useRef<number>(null);

	const router = useRouter();
	const { user } = useUserState();
	const isOpenMap = router.query.map === 'open';
	const isShownEmptyState = (warehouses.length === 0 && !isLoading) || isShownNearWarehouses;
	const bannerAfterIndex = Math.min(3, warehouses?.length ?? 0);
	const nearestWarehouse = useMemo(() => {
		if (!warehouses || warehouses?.length < 1) return null;
		let result = {
			distance: Infinity,
			latitude: 0,
			longitude: 0,
		};

		warehouses.forEach((w) => {
			if (w.distance < result.distance)
				result = {
					distance: w.distance,
					latitude: w.geo_latitude,
					longitude: w.geo_longitude,
				};
		});

		return result;
	}, [warehouses]);

	const handleScrollToItem = (warehouseId: string) => {
		const element = document.querySelector(`[data-scroll-id="${warehouseId}"]`);

		if (element) {
			setHighlightedWarehouseId(warehouseId);
			if (highlightTimerRef.current !== null) {
				window.clearTimeout(highlightTimerRef.current);
			}

			highlightTimerRef.current = window.setTimeout(() => {
				setHighlightedWarehouseId(null);
				highlightTimerRef.current = null;
			}, 1000);

			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	useEffect(() => {
		return () => {
			if (highlightTimerRef.current !== null) window.clearTimeout(highlightTimerRef.current);
		};
	}, []);

	useAsyncEffect(async () => {
		const warehouseId = router.query.warehouseId;
		if (!warehouseId) return;

		setIsModalOpen(true);
		setIsLoadingWarehouseDetails(true);

		try {
			const warehouse = await getWarehouse(warehouseId as string);
			setWarehouseDetails(warehouse);
		} catch (error) {
			console.error('Error while getting warehouse data', error);
		} finally {
			setIsLoadingWarehouseDetails(false);
		}
	}, []);

	const handleWarehouseClick = async (storage: SearchedWarehouse) => {
		setIsModalOpen(true);
		setSelectedWarehouse(storage);
		setWarehouseDetails(null);
		setIsLoadingWarehouseDetails(true);
		void router.push(
			{
				pathname: router.pathname,
				query: { ...router.query, warehouseId: storage.id },
			},
			undefined,
			{ shallow: true },
		);

		try {
			const warehouse = await getWarehouse(storage.id);
			setWarehouseDetails(warehouse);
		} catch (error) {
			console.error('Error while getting warehouse data', error);
		} finally {
			setIsLoadingWarehouseDetails(false);
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setSelectedWarehouse(null);
		setWarehouseDetails(null);

		const { warehouseId, ...cleanedQuery } = router.query;
		void router.push(
			{
				pathname: router.pathname,
				query: cleanedQuery,
			},
			undefined,
			{ shallow: true },
		);
	};

	return (
		<div className={styles.results}>
			<div className={cn({ [styles.loadingWrapper]: isLoading })}>
				<div className={styles.headerWrapper}>
					<FishToBuyResultHeader count={isShownNearWarehouses ? 0 : count} />
				</div>
				{nearestWarehouse && (
					<WarehousesMap
						mapId="personal-account-warehouse-search"
						center={{
							latitude: nearestWarehouse.latitude,
							longitude: nearestWarehouse.longitude,
						}}
						fitToMarkers
						markersInfo={warehouses?.map((w) => ({
							id: w.id,
							latitude: w.geo_latitude,
							longitude: w.geo_longitude,
							location: w.location,
							title: w.title,
						}))}
						onPopupClick={handleScrollToItem}
						isOpenMapByDefault={isOpenMap}
					/>
				)}
				{isShownEmptyState && (
					<Empty
						className={styles.emptyState}
						description={
							<div className={styles.emptyStateContent}>
								<div className={styles.emptyTitle}>{t('search-result.warehouses-not-found')}</div>
								<div className={styles.emptyDescription}>
									{t('search-result.change-query-or-increase-radius')}
								</div>
							</div>
						}
					/>
				)}

				{isShownNearWarehouses && (
					<div className={styles.nearHeaderWrapper}>
						<FishToBuyResultHeader
							title={t('search-result.warehouses-with-similar-parameters')}
							isNearWarehouses
							count={warehouses?.length}
						/>
					</div>
				)}

				<InfiniteScroll
					pageStart={1}
					loadMore={onLoadMore}
					initialLoad={false}
					hasMore={hasMore && !loadMoreDisabled}
					loader={isLoadingMore ? <Spin size="large" /> : null}
				>
					<Row gutter={[0, 12]}>
						{warehouses?.map((storage, index) => (
							<Fragment key={storage.id}>
								<Col span={24}>
									<div className={styles.warehouseScrollTarget} data-scroll-id={storage.id}>
										<NearbyWarehouseCard
											id={storage.id}
											title={storage.title}
											location={storage.location}
											distance={storage.distance}
											temperatures={storage.temp_regimes ? Object.values(storage.temp_regimes) : []}
											hasInspectionRoom={storage.has_inspection_room}
											refContainers={storage.available_for_refrigerators}
											loadType={storage.loading_type}
											hasApplications={storage.has_applications}
											highlighted={highlightedWarehouseId === storage.id}
											onClick={() => handleWarehouseClick(storage)}
										/>
									</div>
								</Col>
								{!user && bannerAfterIndex > 0 && index + 1 === bannerAfterIndex && (
									<Col span={24}>
										<BannerForRegistration />
									</Col>
								)}
							</Fragment>
						))}
						{warehouses?.length === 0 && !user && (
							<Col span={24}>
								<BannerForRegistration />
							</Col>
						)}
					</Row>
				</InfiniteScroll>
			</div>
			{isLoading && (
				<div className={styles.loadingSpinner}>
					<Spin size="large" />
				</div>
			)}

			<Modal
				open={isModalOpen}
				title={
					<div className={styles.modalTitle}>
						<SnowflakeIcon />
						{warehouseDetails?.title || selectedWarehouse?.title}
					</div>
				}
				footer={null}
				width={1000}
				className={styles.warehouseModal}
				onCancel={handleModalClose}
			>
				<Spin spinning={isLoadingWarehouseDetails}>
					<WarehouseModalContent
						isLoadingWarehouseDetails={isLoadingWarehouseDetails}
						selectedWarehouse={selectedWarehouse}
						warehouseDetails={warehouseDetails}
						hasInspectionRoom={
							selectedWarehouse?.has_inspection_room ?? warehouseDetails?.has_inspection_room
						}
						refContainers={
							selectedWarehouse?.available_for_refrigerators ??
							+warehouseDetails?.number_sockets_connect_ref_containers > 0 // Та же самая логика что и в warehouse-search для каждого склада
						}
						loadType={
							selectedWarehouse?.loading_type ??
							(warehouseDetails?.loading_unloading_type ? 'automatic' : 'manual') // Та же самая логика что и в warehouse-search для каждого склада
						}
					/>
				</Spin>
			</Modal>
		</div>
	);
};
